// The scope lives in navigation.ts, where all eight of this console's cross-boundary scopes are
// collected so "what can this console reach" is answerable from one file. Re-exported so a reader
// of this module finds it, and so channel-scopes.mjs sees one literal rather than two.
export { EVENT_CATALOGUE_SCOPE, DEAD_LETTERS_SCOPE } from "./navigation.ts";

import type { PlatformCatalogueEntry } from "@tdelivr/api-client/generated/event-platform/event-catalog.js";
import type { PlatformDeadLetterSummary } from "@tdelivr/api-client/generated/event-platform/dead-letter-management.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow, type DetailField } from "./board.ts";

/**
 * What the event catalogue and dead letters board does to the two listings it reads, away from any
 * rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `dlq`, against **two** operations published an
 * hour apart for this one page:
 *
 *   - EVT-03 `GET /v1/platform/dead-letters` —
 *     [ADR 0059](../../../docs/adr/0059-a-platform-scoped-dead-letter-listing-is-a-separate-operation-and-a-separate-scope.md),
 *     which is EVT-03's **first public operation of any kind**: before it, the only thing a browser
 *     could call on that service was `/v1/service-info`;
 *   - EVT-02 `GET /v1/platform/event-catalog` —
 *     [ADR 0058](../../../docs/adr/0058-a-platform-scoped-event-catalogue-listing-is-a-separate-operation-and-its-cursor-cannot-be-the-unique-constraint.md),
 *     whose tenant-bounded sibling exists, answers eighteen fields, and is on `/internal/v1` behind
 *     a `(visibility='global' OR tenant_id=$1)` bound — so it fails ADR 0029 §2 and ADR 0048 both.
 *
 * ```
 * dlq:['Event catalogue and dead letters','EVT-02 · EVT-03',
 *   'Events that couldn\'t be delivered wait here with their reason, so none are silently lost.',
 *   [['Dead letters','7','failed','unprocessed'],['Oldest','3 h','caution','age'],
 *    ['Redriven today','41','done','delivered'],['Poison','1','failed','fails every time']],
 *   ['Event','Consumer','Reason'],
 *   [['evt_88K2 payments.settled','finance-ledger','schema v2 not accepted','failed','dead-lettered'],
 *    ['evt_91AA delivery.exception.opened','notifications','provider timeout','caution','retrying 2 of 5'],
 *    ['evt_77Q0 trip.state.changed','search-index','poison message','failed','needs a human']],
 *   'warn','Redrive is idempotent: the consumer ignores events it has already processed.']
 * ```
 *
 * ## Two listings, one grid, and the design is what decides that
 *
 * This is the only page of this console whose source chip names two services that both answer
 * something, and the composition is not two grids. **The design draws one grid, and all three of
 * its columns and all four of its tiles are the dead letters'** — `evt_88K2`, `finance-ledger`,
 * `schema v2 not accepted`, `Dead letters · 7`, `Poison · 1`. The catalogue has no column of its own
 * and no tile of its own; it is in the page's title and in its source chip. `CLAUDE.md` section 1
 * forbids adding a section the design does not draw as plainly as it forbids removing one, so a
 * second grid of catalogue entries is not available here however much the title invites it.
 *
 * So the rows are EVT-03's and the catalogue is read for what the design's own grid references: the
 * **event** each dead letter belongs to. `docs/proposals/design-evt.md`'s EVT-02 table says so in
 * its own words — "`Event` — `payments.settled` as a documented event type ← `event_catalog.
 * event_type`", and "owner, lifecycle, visibility (not columns of this grid, but the catalogue's
 * identity)" — and the design's detail panel is where this file puts the catalogue's identity,
 * because the panel is where the design keeps the fields a row's three columns cannot carry.
 *
 * **A catalogue entry no dead letter names is therefore not drawn, and that is a consequence worth
 * stating rather than hiding.** The board is a board of dead letters that says what the platform
 * documents about each one's event. It is not a browsable catalogue, because the design draws no
 * grid for one. Proposed as **Q-217** below with the join it rests on.
 *
 * ## The join is a name match and nothing publishes a key — Q-217
 *
 * **There is no published relation between a dead letter and a catalogue entry.** EVT-03 holds a
 * `topic`, `^[a-z][a-z0-9._-]{2,199}$` (`src/domain.ts`), which is a broker topic. EVT-02 holds an
 * `eventType`, `^[a-z][a-z0-9.-]{2,127}\.v[1-9][0-9]*$`, and a `schemaSubject`,
 * `^[a-z][a-z0-9.-]{2,127}$` — the same name with and without a version suffix. No foreign key, no
 * `catalogId` on a dead letter, no `topic` on a catalogue entry. The two vocabularies overlap and
 * are not the same vocabulary: a topic may carry underscores and may be two hundred characters, and
 * neither is sayable as an event type.
 *
 * `catalogueFor` therefore matches on **name equality only**, exactly, never on a prefix and never
 * on a normalised form, and answers one of three things rather than guessing:
 *
 *   - **one entry** — the catalogue documents an event by exactly that name, for this dead letter's
 *     own tenant or globally;
 *   - **nothing** — the panel draws the design's own `—` with the reason on it;
 *   - **more than one** — also `—`, with how many matched on the cell, because picking one of two
 *     entries that both carry the name would be a row asserting an owner the platform did not.
 *
 * A prefix or suffix rule would have been the improvisation `CLAUDE.md` section 2 forbids: it would
 * read `payments.settled.v2` as documenting a topic named `payments.settled.v2.retry`, and a board
 * naming the wrong owning team beside a failed payment event is worse than a board naming none.
 *
 * ## What the two listings publish, and what they deliberately do not
 *
 * EVT-03's nine: `tenantId`, `topic`, `deadLetterId`, `failureCode`, `risk`, `status`, `version`,
 * `createdAt`, `updatedAt`. Withheld, each for ADR 0059's reason: **`messageReference`** — a
 * durable, resolvable address for another tenant's failed message, which discloses nothing by itself
 * and everything to whoever can reach the broker; **`resolutionReason`** — prose one operator wrote
 * about a live incident; **`requestedBy`** and **`approvals`** — a slice of one operator's roster,
 * held under a dual-control rule that needs the subjects to enforce and does not need them to draw.
 *
 * EVT-02's eleven: `catalogId`, `eventType`, `schemaSubject`, `visibility`, `tenantId`,
 * `ownerServiceId`, `ownerTeam`, `lifecycle`, `version`, `createdAt`, `updatedAt`. Withheld, each
 * for ADR 0058's reason: the **summary** and **documentation URL** (prose an owning team wrote about
 * one operator's product), the **tags** (that operator's taxonomy), the **whole stored document**
 * (which is also the only home of the deprecation reason, the replacement event type and the sunset
 * instant), and the **search vector** (a tsvector built over the summary, so publishing it would
 * re-publish the prose in another shape).
 *
 * Neither absence costs this board a cell. Every field it wanted from both listings is published.
 *
 * ## The three cells nothing can fill
 *
 * **`Consumer`, which is one of the grid's three columns.** `finance-ledger`, `notifications`,
 * `search-index`. **Nothing on this platform models a dead letter's consumer**: it is not a column
 * of `dead_letter_cases`, not a field of `DeadLetterCase`, not in either contract's schemas, and
 * EVT-02's catalogue models an event's **owner** — `ownerServiceId`, `ownerTeam` — rather than its
 * subscribers. Whoever re-delivers is explicitly not EVT-03's responsibility ("retry execution" is
 * a declared non-responsibility), so a consumer name would have to be added to the receive command
 * before any route could answer it. Drawn as the design's own `—` with the reason on the cell,
 * never filled from `ownerTeam` — an owner is not a consumer, and a column headed `Consumer`
 * showing an owning team would be a wrong answer rather than a missing one. **Q-198**, and ADR 0058
 * §What-this-does-not-do and ADR 0059 §Context both decline it as route work for whoever owns the
 * broker consumer.
 *
 * **The attempt counter, which is one badge and three of the four tiles.** `retrying 2 of 5`,
 * `Poison · 1 · fails every time` and `Redriven today · 41 · delivered` are one absence in three
 * shapes: **nothing counts attempts.** `retry_authorized` is a status rather than a dated retry
 * event, and the dates that do exist are in `dead_letter_audit`, which is published nowhere. So the
 * badge is drawn from `status` alone — `retrying`, without the fraction — which is what ADR 0059 §7
 * settles, and the tiles are not drawn at all. **Q-199**.
 *
 * **The join above, Q-217**, which is this board's own third finding rather than one it inherited.
 *
 * Two more differences that are recorded elsewhere and are visible on every row:
 *
 *   - **`Oldest · 3 h · age`** is the fourth tile and is the R-04 family rather than Q-199's: it is
 *     the age of the oldest dead letter in the **store**, and this listing answers a page ordered by
 *     `(tenant_id, topic, dead_letter_id)`. The first page is not the oldest page, so a tile
 *     computed over the rows in hand would read "the oldest of the ones that happened to arrive"
 *     under a label meaning "the oldest there is". Q-178 carries the tile row across this console.
 *   - **`evt_88K2` is eight characters and `deadLetterId` is a UUID.** ADR 0059 §7 records it and
 *     says the board writes what it can prove. Q-196's family, and the same divergence the audit and
 *     flags boards already draw against this design file.
 */

/**
 * The scope EVT-02's platform catalogue listing is bought with.
 *
 * A literal in this application's source, which is what grants it: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name, so
 * naming it here is the grant and nothing else is. ADR 0032's mechanism, ADR 0049's rule that a
 * channel names only the scopes its drawn screens spend, and ADR 0058 §2 on why
 * `event-catalog:read` is not widened instead — it is held by workloads discovering their own
 * tenant's documented events, and widening it would put a cross-tenant read on every token that
 * holds the workload read.
 *
 * **Q-139 takes its sixth occasion here.** Nobody has decided who may hold a scope that reads across
 * every tenant on the platform, and this board names two of them.
 */

/**
 * The scope EVT-03's platform dead-letter listing is bought with.
 *
 * ADR 0059 §2. `dead-letter:read` buys the tenant-bounded by-identifier read and is not widened; the
 * six command scopes each buy a lifecycle decision, and `dead-letter:retry-approve` is half of a
 * two-person control.
 */

/**
 * The design's `Reason` column: `schema v2 not accepted`, `provider timeout`, `poison message`.
 *
 * `failureCode` is `^[A-Z][A-Z0-9_]{2,63}$` — a code, which is exactly why it can be published
 * across a tenancy boundary at all: `SCHEMA_V2_NOT_ACCEPTED` names a class of failure and describes
 * no tenant's business. ADR 0059 §Context says the design's cell "is that code rendered in sentence
 * case by the client", and this is that rendering: underscores to spaces, lowered.
 *
 * **All three of the design's `Reason` cells come out word for word** from the three codes behind
 * them, which is the measurement that says the grammar is the right one rather than a guess at it.
 * The first letter is not capitalised, because the design does not capitalise it.
 *
 * The published code is kept on the cell's `title`, since a code is what an operator searches a
 * runbook for and the sentence-cased form is not.
 */
export function reasonText(failureCode: string): string {
  return failureCode.replaceAll("_", " ").toLowerCase();
}

/**
 * The design's `Event` column: `evt_88K2 payments.settled`.
 *
 * The identifier and the event's name, in the design's own order and separated by its own space.
 * Both halves are EVT-03's: `deadLetterId`, and `topic` — which the proposal's own column table
 * identifies as the event name, "whose sample values in the design _are_ event types".
 *
 * **The identifier is a UUID where the design writes eight characters**, and it is written in full
 * rather than truncated: a dead letter is addressed by `(tenantId, topic, deadLetterId)` and half an
 * identifier addresses nothing. ADR 0059 §7 records the width and says the board writes what it can
 * prove; Q-196 is the same divergence the audit and flags boards draw against this design file.
 */
export function eventText(summary: PlatformDeadLetterSummary): string {
  return `${summary.deadLetterId} ${summary.topic}`;
}

/** What the catalogue can say about one dead letter's event, which is one of three things. */
export type CatalogueMatch =
  | { readonly kind: "one"; readonly entry: PlatformCatalogueEntry }
  | { readonly kind: "none" }
  | { readonly kind: "ambiguous"; readonly count: number };

/**
 * The catalogue entry documenting this dead letter's event, when exactly one does.
 *
 * Name equality, exactly, in two steps and then two narrowings — see this file's header for why a
 * looser rule is not available and Q-217 for the question it leaves open:
 *
 *   1. an entry whose **`eventType`** is the topic, which is a topic named for the versioned event
 *      type (`payments.settled.v2`);
 *   2. failing that, an entry whose **`schemaSubject`** is the topic, which is a topic named for the
 *      subject (`payments.settled`) — the same name without the version suffix.
 *
 * Then, among whatever that answered: this dead letter's **own tenant** if any of them is its, else
 * the **global** entries if any of them is global. Both narrowings are the catalogue's own model
 * rather than a preference invented here — `visibility` is `global | tenant` with
 * `CHECK((visibility='global' AND tenant_id IS NULL) OR (visibility='tenant' AND tenant_id IS NOT
 * NULL))`, so a tenant's own entry is the one that documents that tenant's event and a global entry
 * is the platform's default for the event types every tenant shares.
 *
 * More than one left after both narrowings is answered `ambiguous` and drawn `—`. That is reachable:
 * `UNIQUE (event_type, tenant_id)` does not prevent two global rows with the same event type,
 * because NULLs are distinct under a unique constraint — ADR 0058 names that as a constraint worth
 * revisiting and not its to revisit — and two entries can share a `schemaSubject` across versions by
 * design, since the subject is the name without the version.
 */
export function catalogueFor(
  summary: PlatformDeadLetterSummary,
  entries: readonly PlatformCatalogueEntry[],
): CatalogueMatch {
  const byEventType = entries.filter((entry) => entry.eventType === summary.topic);
  const named =
    byEventType.length > 0
      ? byEventType
      : entries.filter((entry) => entry.schemaSubject === summary.topic);

  const ownTenant = named.filter((entry) => entry.tenantId === summary.tenantId);
  const global = named.filter((entry) => entry.visibility === "global");

  /*
   * This tenant's entry, or a global one, or **nothing** — never another tenant's.
   *
   * The third branch here was `: named`, which falls back to every entry carrying the name. That is
   * a cross-tenant attribution bug on a cross-tenant board: if tenant A dead-letters
   * `payments.settled.v2` and only tenant B has catalogued that event type privately, the panel
   * drew B's owning team, B's lifecycle, B's schema subject and `Catalogue visibility: tenant ·
   * <B's uuid>` as facts about **A's** row, with no uncertainty marker on any of them.
   *
   * It is wrong on the question as well as on the tenancy. A catalogue entry private to another
   * tenant is not evidence about this dead letter at all — tenant B documenting an event type says
   * nothing about the event tenant A failed to deliver, even where the names agree. The honest
   * answer is the one the design already draws for a row the catalogue does not document: an em
   * dash. So `none` it is, and the five panel lines draw `—` exactly as they do for a topic
   * nothing has catalogued.
   *
   * Found by review, not by a test — `catalogueFor` had none, which is recorded below.
   */
  const narrowed = ownTenant.length > 0 ? ownTenant : global;

  if (narrowed.length === 0) return { kind: "none" };
  const only = narrowed[0];
  if (narrowed.length > 1 || only === undefined) {
    return { kind: "ambiguous", count: narrowed.length };
  }
  return { kind: "one", entry: only };
}

/** The states EVT-03's `status` can answer, in the order its own lifecycle reaches them. */
export type DeadLetterState =
  "dead-lettered" | "triaged" | "quarantined" | "needs a human" | "retrying" | "discarded";

/**
 * The badge's class and word, from `status` alone.
 *
 * **Three of the design's words are its own and three states have no designed row**, which is the
 * same split `partner-api.ts` records: the design draws three badges and the contract has six
 * states, so the three it drew are copied word for word and the three it did not are given the
 * contract's own word with the nearest of the token system's nine classes.
 *
 * | `status`                 | class     | word            | whose word                        |
 * | ------------------------ | --------- | --------------- | --------------------------------- |
 * | `received`               | `failed`  | `dead-lettered` | the design's first row            |
 * | `triaged`                | `caution` | `triaged`       | the contract's                    |
 * | `quarantined`            | `failed`  | `quarantined`   | the contract's                    |
 * | `retry_pending_approval` | `human`   | `needs a human` | the design's third row            |
 * | `retry_authorized`       | `caution` | `retrying`      | the design's second row, less `2 of 5` |
 * | `discarded`              | `closed`  | `discarded`     | the contract's                    |
 *
 * Three readings in that table are decisions rather than transcriptions, so each is argued:
 *
 * **`retrying`, without `2 of 5`.** The fraction is the attempt counter nothing publishes, and
 * ADR 0059 §7 settles the badge as drawn from `status` alone for exactly that reason. Q-199.
 *
 * **`needs a human` is `human` where the design's own row writes `failed`, and that is a recorded
 * divergence rather than a liberty.** The design's third sample row is `poison message · failed ·
 * needs a human` — a poison message, which is the `quarantined` shape — and ADR 0059 §7 assigns the
 * words "needs a human" to `retry_pending_approval`. Three things then decide the class, and they
 * agree: `SEMK.human` is this design file's own class for a row that needs a person; its `detailUI`
 * maps `human` to the action `Open approval`, which is precisely the operation this row is waiting
 * for (`approveRetry`, refused for the requester and for a previous approver); and its `_rv` tints a
 * `human` row `--humanRow`, which is the one row tint this console's stylesheet carries. Recorded as
 * part of **Q-217**.
 *
 * **`quarantined` is `failed` and `triaged` is `caution`.** Both are unresolved and neither is
 * waiting on a named person: a quarantined case is held out of the stream and is what the design's
 * `Poison · fails every time` tile is about, so it takes the class the design gives that tile; a
 * triaged case has been read and classified and is waiting on a retry request, which is the amber
 * rather than the red. `discarded` is `closed` on ADR 0059 §7's own word for it.
 *
 * A state is never shown by colour alone: every one of these carries a glyph and a word, and the six
 * words are six different words.
 */
export function stateOf(summary: PlatformDeadLetterSummary): {
  classKey: StateClassKey;
  label: DeadLetterState;
  title?: string;
} {
  switch (summary.status) {
    case "received":
      return {
        classKey: "failed",
        label: "dead-lettered",
        title:
          "Received and not yet triaged. Nothing has looked at it, and no attempt count is published — the design's retry fraction has no source. Q-199.",
      };
    case "triaged":
      return {
        classKey: "caution",
        label: "triaged",
        title:
          "Read and classified, waiting on a retry request. The design has no row for this state; the word is EVT-03's own.",
      };
    case "quarantined":
      return {
        classKey: "failed",
        label: "quarantined",
        title:
          "Held out of the stream. The design's Poison tile counts these and its count has no source. Q-199.",
      };
    case "retry_pending_approval":
      return {
        classKey: "human",
        label: "needs a human",
        title:
          "A retry has been requested and is waiting on an approval this console cannot send: the approval is on /internal/v1, it is a two-person control, and it is enforced against the staff subjects this listing withholds. ADR 0059 §5.",
      };
    case "retry_authorized":
      return {
        classKey: "caution",
        label: "retrying",
        title:
          "The retry is authorised. The design writes a fraction here — retrying 2 of 5 — and nothing counts attempts: retry execution is a declared non-responsibility of EVT-03. Q-199.",
      };
    case "discarded":
      return {
        classKey: "closed",
        label: "discarded",
        title:
          "Closed without a retry. The reason an operator gave is withheld: it is prose about one tenant's live incident. ADR 0059 §4.",
      };
  }
}

/**
 * The catalogue's identity for one dead letter's event, as the detail panel's own lines.
 *
 * Five fields, every one published by EVT-02's platform listing, in the order the catalogue's own
 * record reads them: what the event is called under a schema, who owns it, and where it is in its
 * lifecycle. The panel is where they go because the design's grid has three columns and none of them
 * is an owner — see this file's header.
 *
 * Every line draws the design's own `—` when the catalogue cannot say which entry this is, rather
 * than being dropped: a panel whose field table changes shape row by row would make an absence look
 * like a different record. The reason is under the grid rather than on the line, because
 * `DetailField` carries no `title` and `ui/RowDetail.tsx` is shared with five measured boards —
 * `listingNote` says it once for the board instead.
 */
const CATALOGUE_LABELS = [
  "Schema subject",
  "Owner service",
  "Owner team",
  "Lifecycle",
  "Catalogue visibility",
] as const;

function catalogueFields(match: CatalogueMatch): readonly DetailField[] {
  if (match.kind !== "one") {
    return CATALOGUE_LABELS.map((label) => ({ label, value: "", absent: true }));
  }

  const { entry } = match;
  return [
    { label: "Schema subject", value: entry.schemaSubject, mono: true },
    // The design's own source chip is Geist Mono, which is what a service code is set in here too.
    { label: "Owner service", value: entry.ownerServiceId, mono: true },
    { label: "Owner team", value: entry.ownerTeam },
    { label: "Lifecycle", value: entry.lifecycle },
    {
      label: "Catalogue visibility",
      value: entry.visibility === "global" ? "global" : `tenant · ${entry.tenantId ?? ""}`,
      ...(entry.visibility === "tenant" ? { mono: true } : {}),
    },
  ];
}

/**
 * One row of the event catalogue and dead letters board.
 *
 * @param summary one dead letter, as EVT-03's platform listing answered it.
 * @param entries the catalogue page EVT-02 answered, which the event name is matched against.
 * @param catalogueReachable whether EVT-02 answered at all — see `EventCatalogue.tsx` on why a
 *   catalogue that did not answer degrades five panel lines rather than blanking the board.
 */
export function deadLetterRow(
  summary: PlatformDeadLetterSummary,
  entries: readonly PlatformCatalogueEntry[],
  catalogueReachable: boolean,
): BoardRow {
  const state = stateOf(summary);
  const match = catalogueReachable ? catalogueFor(summary, entries) : { kind: "none" as const };

  return {
    /*
     * The tenant, the topic and the dead letter: EVT-03's primary key and this listing's cursor,
     * `(tenant_id, topic, dead_letter_id)`. Prefixed with the tenant because the listing spans
     * tenants and `(topic, dead_letter_id)` is unique only within one — ADR 0059 §6 — so a React key
     * that collided would make one tenant's row replace another's on a board whose whole purpose is
     * seeing every tenant's.
     */
    id: `${summary.tenantId}:${summary.topic}:${summary.deadLetterId}`,
    cells: [
      {
        text: eventText(summary),
        /*
         * Mono, which is the design's own rule for this cell reached by a different route.
         *
         * Its grid takes Geist Mono for a value matching its prefix list — `ten_`, `evt_`, `RPL`,
         * `RSK`, `wf_`, `/v1`, `key`, `offset` — and the design's own cell here is `evt_88K2 …`,
         * which matches `evt_`. The value this listing publishes starts with a UUID, which matches
         * none of them, so the design's regex read literally would set this cell in Geist. It is the
         * same kind of value under a different width, which is what `board.ts` extends `mono` for
         * and what Q-196 records; drawing an identifier in Geist because its prefix changed would be
         * following the rule's letter past its point.
         */
        mono: true,
        title: `Dead letter ${summary.deadLetterId} on topic ${summary.topic}. The design writes an eight-character identifier; EVT-03's is a UUID. ADR 0059 §7, Q-196.`,
      },
      {
        text: "",
        absent: true,
        title:
          "Nothing on this platform models a dead letter's consumer: it is no column of EVT-03's record and EVT-02's catalogue models an event's owner rather than its subscribers. A consumer name would have to be added to the receive command before any route could answer it. Q-198.",
      },
      {
        text: reasonText(summary.failureCode),
        title: `Published as ${summary.failureCode}. The design writes the code in sentence case, which is what this is.`,
      },
    ],
    state,
    /*
     * The design tints a row for its `human` and `failed` classes — `--humanRow` and `--failRow` —
     * and this console's stylesheet carries one class for the first of them.
     *
     * So a row needing a person is tinted exactly as the design tints it, and a `failed` row is not
     * tinted at all. Tinting it with `--humanRow` instead would be both the wrong colour and a false
     * signal, since that tint means "a person is waited on" everywhere else in this console. The
     * badge beside it carries the glyph and the word either way — a state is never shown by colour
     * alone — so what is lost is a reinforcement rather than the signal. Recorded as part of
     * **Q-217**: `--failRow` is in `@tdelivr/design-tokens` and has no rule in
     * `apps/platform-console/src/styles.css`.
     */
    ...(state.classKey === "human" ? { tinted: true } : {}),
    detail: [
      { label: "Tenant", value: summary.tenantId, mono: true },
      { label: "Topic", value: summary.topic, mono: true },
      { label: "Dead letter", value: summary.deadLetterId, mono: true },
      // Mono, because this is the one place the code appears as published: the grid's `Reason` cell
      // is the same value in the design's sentence case, and a runbook is searched for the code.
      { label: "Failure code", value: summary.failureCode, mono: true },
      /*
       * `risk` is published and the design has no column for it, which is the panel's own purpose.
       *
       * It is what decides how many approvals a retry needs — `financial` and `high_risk` cases need
       * two, and `approveRetry` refuses an approver who is the requester or a previous approver — so
       * it is the field that says how far from being retried a case is. Written in EVT-03's own
       * vocabulary rather than prettified: `high_risk` is the published value.
       */
      { label: "Risk", value: summary.risk },
      ...catalogueFields(match),
    ],
    /*
     * The design's `History` block with the two entries a dead letter genuinely has, and the third.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. EVT-03 records who requested a retry
     * and who approved it and this listing withholds both — they are a slice of one operator's
     * roster, ADR 0059 §Context — so these are the instants without the names. Q-107 is where the
     * missing actor on every `History` block lives.
     */
    history: [
      { label: "Created", value: asDay(summary.createdAt) },
      { label: "Last changed", value: asDay(summary.updatedAt) },
      { label: "Version", value: `v${String(summary.version)}` },
    ],
    /*
     * The design's own glyph for this panel, which is `info` and is not the page's nav glyph.
     *
     * Its panel head renders `fm.icon`, and `fm.icon` comes from the page's entry in `FORMS` —
     * except that `FORMS` has **no `dlq` entry at all**, so `formUI` takes its `if(!F)` branch and
     * returns `icon:'info'`. That is what the reference file draws, and `CLAUDE.md` section 1's
     * acceptance check is a comparison against the reference file rather than against its intent.
     * The page's rail glyph is `mark_email_unread`; it is not what this tile shows.
     */
    glyph: "info",
  };
}

/** The states this listing can answer, in the order `stateOf` decides them. */
export const DEAD_LETTER_STATES: readonly DeadLetterState[] = [
  "dead-lettered",
  "triaged",
  "quarantined",
  "needs a human",
  "retrying",
  "discarded",
];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(
  summaries: readonly PlatformDeadLetterSummary[],
): readonly DeadLetterState[] {
  const present = new Set(summaries.map((summary) => stateOf(summary).label));
  return DEAD_LETTER_STATES.filter((label) => present.has(label));
}

/**
 * What the board owes an operator about the two listings behind it, in one sentence each.
 *
 * Three things, and each is said rather than implied because each invites a stronger reading than the
 * data supports:
 *
 *   - **how many tenants the rows came from**, because the page is a board of every dead letter on
 *     the platform and cannot show that it is: nothing publishes how many tenants exist — `POST
 *     /v1/tenants` exists and there is no `GET` — so "7 across 5 of 7 tenants" is unsayable and
 *     "dead letters from 3 tenants" is exactly true. `partner-api.ts`'s `tenantSpanNote` and
 *     `platform-audit.ts`'s are the same sentence for the same reason.
 *   - **how many rows the catalogue could name**, because the page is titled for the catalogue and
 *     the catalogue is in the detail panel, where an operator who does not open a row never sees
 *     whether it answered.
 *   - **that the `Consumer` column has no source**, which is a third of the grid and is the one
 *     absence a reader cannot discover by hovering a cell they did not think to hover.
 *
 * @param summaries the dead letters on the board, or `undefined` before they have arrived.
 * @param entries the catalogue page, or `undefined` when EVT-02 did not answer.
 */
export function listingNote(
  summaries: readonly PlatformDeadLetterSummary[] | undefined,
  entries: readonly PlatformCatalogueEntry[] | undefined,
): string | undefined {
  if (summaries === undefined) return undefined;
  const tenants = new Set(summaries.map((summary) => summary.tenantId)).size;
  if (tenants === 0) return undefined;

  const span = `Dead letters from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}.`;
  const consumer =
    "The Consumer column has no source: nothing models a dead letter's consumer, and an event's owner is not one.";

  if (entries === undefined) {
    return `${span} EVT-02's catalogue did not answer, so no event's owner or lifecycle is shown. ${consumer}`;
  }

  const named = summaries.filter((summary) => catalogueFor(summary, entries).kind === "one").length;
  return `${span} ${String(named)} of ${String(summaries.length)} name an event the catalogue documents, matched on the name alone — nothing publishes a relation between a topic and a catalogue entry. ${consumer}`;
}

/**
 * Why this page's primary command is drawn and refused, and why it is drawn at all.
 *
 * **The design draws no primary button on this page.** `FORMS` has no `dlq` entry, so its `formUI`
 * takes the `if(!F)` branch and returns `btnS:{display:'none'}` — and its detail panel's `Edit`
 * takes `editS:{display: F ? 'inline-flex' : 'none'}` and is hidden for the same reason. `ui/
 * RecordsBoard.tsx` and `ui/RowDetail.tsx` draw both unconditionally, and both are shared with five
 * measured boards and are not this change's to alter. So this page draws two controls the design
 * does not, disabled, with the reason on them — a recorded divergence, part of **Q-217**, whose fix
 * is a `primary` that may be omitted and an `Edit` that is conditional.
 *
 * The word on the button is the design's own for the one command this page offers anywhere: its
 * detail panel maps a `failed` row to `Retry now` and a `caution` row to `Fix issue`. `Retry now` is
 * taken rather than a word invented for a button the design never drew.
 *
 * It could not be sent in any case, and there are three reasons:
 *
 *   - **Every write EVT-03 publishes is `/internal/v1`** — receive, triage, quarantine,
 *     retry-request, retry-approve, discard — which the gateway does not expose, and ADR 0059 §5
 *     answers 404 on all five platform-shaped paths deliberately.
 *   - **A retry needs two different people.** `approveRetry` refuses an approver who is the
 *     requester or a previous approver, and `financial` and `high_risk` cases need two approvals, so
 *     one administrator pressing this could not satisfy it even if the route were public.
 *   - **The independence of that pair is enforced against the subjects this listing withholds**, so
 *     this console holds neither half of what the control compares.
 *
 * ADR 0059 §5's closing sentence is the one worth reading beside the design's own note: "the
 * design's note that 'redrive is idempotent' therefore describes an action this console cannot
 * take." The note is drawn anyway, word for word, because it is the design's copy.
 */
/**
 * Why this page draws no command, which is now a property of the design rather than a refusal.
 *
 * This was a `CREATE_REFUSAL` string on a disabled button. The button is gone: the design file has
 * no `FORMS.dlq` entry, so its own `formUI` returns `btnS:{display:'none'}` and `editS` likewise,
 * and `RecordsBoard` drew one anyway because its `primary` prop was required. Drawing a disabled
 * control the design omits is an added section, which `CLAUDE.md` §1 forbids in the same breath as
 * removing one — so `primary` is optional now and this board passes none.
 *
 * The reasoning the string carried is still true and is kept here rather than deleted with it:
 * EVT-03 publishes every write on `/internal/v1` only, which the gateway does not expose, and a
 * retry needs two different people enforced against the staff subjects this listing withholds. So
 * even had the design drawn a command, this console could not send it. ADR 0059 §5, Q-217.
 */
